import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import startOfDay from 'date-fns/startOfDay';

import FormGroup from '../../../../../../../components/global/FormGroup';
import Alert from '../../../../../../../components/global/Alert';
import { getFormikTouchedErrors } from '../../../../../../../util/formik';
import Label from '../../../../../../../components/global/Label';
import DatePickerWithYearDropdown from '../../../../../../../components/global/DatePickerWithYearDropdown';
import InputGroup from '../../../../../../../components/global/InputGroup';
import { getAsset } from '../../../../../../../util/assets';
import { EVENT_DETAILS_CONTEXT } from '../utils';
import { getNameWithPossessive } from '../../SpecialtyAlbumGreetingModal/helpers';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    start_date: PropTypes.object,
    end_date: PropTypes.object,
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  requireAccountEmail: PropTypes.bool,
  requireCouplesEmail: PropTypes.bool,
  allowAutoComplete: PropTypes.bool,
  context: PropTypes.oneOf(Object.values(EVENT_DETAILS_CONTEXT)),
};

const WeddingEventDetails = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  requireAccountEmail,
  requireCouplesEmail,
  allowAutoComplete,
  context,
}) => {
  const dateInputRef = useRef();
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));
  const { date } = values;

  useEffect(() => {
    if (dateInputRef?.current) {
      dateInputRef.current.readOnly = 'readonly';
    }
  }, [dateInputRef]);

  const autoCompleteEventName = () => {
    if (!values.bride_name && !values.groom_name) {
      return;
    }

    const baseName = values.bride_name || values.groom_name;
    const bothNamesAvailable = values.bride_name && values.groom_name;

    const eventName = `${
      bothNamesAvailable
        ? `${getNameWithPossessive(`${baseName} & ${values.groom_name}`)}`
        : `${baseName}`
    } Wedding ❤️`;

    handleChange({
      target: {
        name: 'name',
        value: eventName,
      },
    });
  };

  useEffect(() => {
    if (allowAutoComplete) {
      autoCompleteEventName();
    }
  }, [values.bride_name, values.groom_name]);

  return (
    <Fragment>
      <div className="row">
        <FormGroup
          id="bride_name"
          name="bride_name"
          error={!!errors.bride_name}
          label="Who's getting married?"
          placeholder="Bride / Groom's first name"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.bride_name}
          className="freemium-bride-name-form-group col-12 col-md-6"
        />
        <div className="col-12 d-lg-none text-center">&amp;</div>
        <FormGroup
          id="groom_name"
          name="groom_name"
          error={!!errors.groom_name}
          label={<>&nbsp;</>}
          labelProps={{
            className: 'd-none d-lg-block',
          }}
          placeholder="Bride / Groom's first name"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.groom_name}
          className="col-12 col-md-6"
        />
      </div>

      <FormGroup
        id="name"
        name="name"
        error={!!errors.name}
        label={
          context === EVENT_DETAILS_CONTEXT.SLIDESHOW
            ? 'Slideshow Name'
            : 'Event Name'
        }
        placeholder="Enter event name here..."
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.name}
      />

      {requireCouplesEmail && (
        <FormGroup
          id="coupleEmailAddress"
          name="couple_email_address"
          error={!!errors.couple_email_address}
          label="Bride / groom’s email"
          placeholder="name@example.com"
          required
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.couple_email_address}
        />
      )}
      {requireAccountEmail && (
        <FormGroup
          id="emailAddress"
          name="email_address"
          error={!!errors.email_address}
          label="Your email address"
          placeholder="name@example.com"
          required
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email_address}
        />
      )}
      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <Label id="start_date">Wedding Date</Label>
            <div className="datepicker-gray-light">
              <DatePickerWithYearDropdown
                id="date"
                name="date"
                customInput={
                  <InputGroup
                    id="date"
                    name="date"
                    error={!!errors.date}
                    type="text"
                    autoComplete="off"
                    inputRef={dateInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={date}
              />
            </div>
          </div>
        </div>
      </div>

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

WeddingEventDetails.propTypes = propTypes;

export default WeddingEventDetails;
